/**
 * loop.ts — drives the fixed-tick simulation from requestAnimationFrame.
 *
 * Strategy: decouple sim rate from frame rate. The rAF callback accumulates
 * real elapsed time and calls advanceTick as many times as that budget allows,
 * producing exactly 10 ticks per real second on any hardware (MECHANICS §1.1).
 *
 * Pause and speed controls belong to the UI layer; they feed a `speed` number
 * into the loop (1 = real-time, 0 = paused, 2/4 = fast-forward). The user's
 * local speed does NOT affect multiplayer sim cadence — that's pinned at 1.
 */

import { advanceTick } from './advance';
import { applyCommands, type Command } from './commands';
import type { GameState } from '../state/types';

export const SIM_TICK_RATE_HZ = 10;
export const SIM_TICK_MS = 1000 / SIM_TICK_RATE_HZ;

export interface SimLoopHandle {
  stop(): void;
  setSpeed(speed: number): void;
  getSpeed(): number;
  queueCommand(cmd: Command): void;
  /** Force-drive the loop by N ticks (for tests). Does not use rAF. */
  stepManual(ticks: number): void;
}

export interface SimLoopOptions {
  /**
   * Called immediately after each tick advance, with the freshly mutated
   * state. Renderer should re-draw here, HUD should re-read values.
   */
  onTick?: (state: GameState) => void;
  /** Initial speed multiplier (default 1). 0 pauses the loop. */
  initialSpeed?: number;
  /** Injection seams for tests. */
  now?: () => number;
  schedule?: (cb: () => void) => number;
  cancel?: (handle: number) => void;
}

/** Create and start a simulation loop. */
export function startSimLoop(state: GameState, opts: SimLoopOptions = {}): SimLoopHandle {
  const now = opts.now ?? (() => performance.now());
  const schedule =
    opts.schedule ?? ((cb: () => void) => requestAnimationFrame(cb));
  const cancel = opts.cancel ?? ((h: number) => cancelAnimationFrame(h));

  let speed = opts.initialSpeed ?? 1;
  let running = true;
  let rafHandle: number | null = null;
  let accumulator = 0;
  let lastTime = now();
  const commandQueue: Command[] = [];

  function frame(): void {
    if (!running) return;
    const t = now();
    const dt = t - lastTime;
    lastTime = t;
    if (speed > 0) {
      accumulator += dt * speed;
      // Cap accumulator to avoid "spiral of death" after tab sleep: at most 250ms worth.
      if (accumulator > 250) accumulator = 250;
      while (accumulator >= SIM_TICK_MS) {
        if (commandQueue.length > 0) {
          const batch = commandQueue.splice(0, commandQueue.length);
          applyCommands(state, batch);
        }
        advanceTick(state);
        opts.onTick?.(state);
        accumulator -= SIM_TICK_MS;
      }
    }
    rafHandle = schedule(frame);
  }

  rafHandle = schedule(frame);

  return {
    stop(): void {
      running = false;
      if (rafHandle !== null) cancel(rafHandle);
      rafHandle = null;
    },
    setSpeed(v: number): void {
      speed = Math.max(0, v);
    },
    getSpeed(): number {
      return speed;
    },
    queueCommand(cmd: Command): void {
      commandQueue.push(cmd);
    },
    stepManual(ticks: number): void {
      for (let i = 0; i < ticks; i++) {
        if (commandQueue.length > 0) {
          const batch = commandQueue.splice(0, commandQueue.length);
          applyCommands(state, batch);
        }
        advanceTick(state);
        opts.onTick?.(state);
      }
    },
  };
}
